import Selector from "./Components/Selector";
import useAuth from "../Users/hooks/useAuth";
import { useEffect, useState } from "react";
import UploadFile from "./Components/UploadFile";
import BookTitle from "./Components/BookTitle";
import DropdownLang from "./Components/DropdownLang";
import DropdownType from "./Components/DropdownType";

const BookCase = ({ successHandler }) => {
    const { auth, setAuth } = useAuth();
    const [lang, setLang] = useState("Define the language");
    const [bookcase, setBookcase] = useState([]);
    const [newTag, setTag] = useState("");
    const [newAuthor, setAuthor] = useState("");
    const [changeList, setChangeList] = useState([]);
    const [changedTags, setChangedTags] = useState([]);
    const [currentBook, setBook] = useState("");
    const [pages, setPages] = useState(0);
    const tabs = ['Books management'];
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [newBookcaseError, setNewBookcaseError] = useState("");
    const [type, setType] = useState("Book or movie?");
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Sorting state
    const [sortBy, setSortBy] = useState("date"); // "title", "author", "language", or "date"
    const [sortOrder, setSortOrder] = useState("desc"); // "asc" or "desc" - default to newest first

    const fetchData = async (route, method) => {
        const token = localStorage.getItem("token");
        const url = `${process.env.REACT_APP_USER_API_URL}/bookcase/${route}`;
        const response = await fetch(url, {
            method: method,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            }
        });
        if (response.status === 401) {
            localStorage.removeItem("token");
            setAuth({});
            return null;
        }
        if (response.ok) {
            const json = await response.json();
            return json;
        }
        return null;
    }

    const createBookcase = async ({ author, title, language, type }) => {
        await fetchData(`create-bookcase/${author}/${title}/${language}/${type}`, "POST");
    }

    const getBookcase = async () => {
        const json = await fetchData("get-bookcase", "GET");
        if (json) {
            setBookcase(json);
            setCurrentPage(1); // Reset to first page when bookcase updates
        }
    }

    const deleteBookcase = async ({ author, title, language, type }) => {
        await fetchData(`delete-bookcase/${author}/${title}/${language}/${type}`, "DELETE");
    }

    const getPages = async () => {
        const json = await fetchData(`pages/${currentBook}`, "GET");
        if (json) {
            setPages(json.pages);
        }
    }

    const commit = async () => {
        for (const change of changeList) {
            const splits = change.action.split(" ");
            switch (splits[0]) {
                case "DELETE":
                    await deleteBookcase({ author: change.author, title: change.title, language: change.language, type: change.type });
                    break;
                case "CREATE":
                    await createBookcase({ author: change.author, title: change.title, language: change.language, type: change.type });
                    break;
            }
        }
        setChangeList([]);
        setChangedTags([]);
        getBookcase();
    }

    const removeChange = (i) => {
        let array = [...changeList];
        array.splice(i, 1);
        setChangeList(array);
    }

    const addChange = (change) => {
        setChangeList([...changeList, change]);
    }

    const addCreateBookcase = (event) => {
        event.preventDefault();
        setNewBookcaseError("");
        
        const bookExists = bookcase.some(book => 
            book.author === newAuthor && 
            book.tag === newTag && 
            book.language === lang && 
            book.type === type
        );

        if (bookExists) {
            setNewBookcaseError("Book with this name already exists.");
        } else if (newTag === "") {
            setNewBookcaseError("Name for book required");
        } else if (newAuthor === "") {
            setNewBookcaseError('The name of the author is required');
        } else if (lang === 'Define the language') {
            setNewBookcaseError("The language of the book required");
        } else if (type === 'Book or movie?') {
            setNewBookcaseError("What is this, a book or movie?");
        } else {
            addChange({
                author: newAuthor,
                title: newTag,
                action: `CREATE`,
                language: lang,
                type: type
            });
            // Clear form after successful addition
            setAuthor("");
            setTag("");
            setLang("Define the language");
            setType("Book or movie?");
        }
    }

    const addDeleteBookcase = ({ author, title, language, type }) => {
        addChange({
            author: author,
            title: title,
            type: type,
            action: "DELETE",
            language: language
        });
    }

    // Sorting function
    const sortBookcase = (books) => {
        return [...books].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortBy) {
                case "title":
                    aValue = a.tag.toLowerCase();
                    bValue = b.tag.toLowerCase();
                    break;
                case "author":
                    aValue = a.author.toLowerCase();
                    bValue = b.author.toLowerCase();
                    break;
                case "language":
                    aValue = a.language.toLowerCase();
                    bValue = b.language.toLowerCase();
                    break;
                case "date":
                default:
                    // For date sorting, we use the array index as a proxy for creation order
                    // Since the database returns them in creation order, we can use the index
                    // If your API returns them in creation order, this will work
                    aValue = books.indexOf(a);
                    bValue = books.indexOf(b);
                    break;
            }

            if (sortBy === "date") {
                // For date, we want to maintain the original order but allow reversal
                return sortOrder === "desc" ? bValue - aValue : aValue - bValue;
            } else {
                // For other fields, use alphabetical sorting
                if (aValue < bValue) {
                    return sortOrder === "asc" ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortOrder === "asc" ? 1 : -1;
                }
                return 0;
            }
        });
    };

    // Toggle sort order
    const handleSort = (field) => {
        if (sortBy === field) {
            // Toggle order if same field
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            // New field, set appropriate default order
            setSortBy(field);
            if (field === "date") {
                setSortOrder("desc"); // Newest first by default for date
            } else {
                setSortOrder("asc"); // A-Z by default for other fields
            }
        }
        setCurrentPage(1); // Reset to first page when sorting changes
    };

    // Get sort icon
    const getSortIcon = (field) => {
        if (sortBy !== field) return "↕️";
        return sortOrder === "asc" ? "↑" : "↓";
    };

    // Get sort label
    const getSortLabel = (field) => {
        const labels = {
            "date": "Date Added",
            "title": "Title",
            "author": "Author",
            "language": "Language"
        };
        return labels[field] || field;
    };

    // Get current sort description
    const getSortDescription = () => {
        if (sortBy === "date") {
            return sortOrder === "desc" ? "Newest First" : "Oldest First";
        } else {
            return sortOrder === "asc" ? "A → Z" : "Z → A";
        }
    };

    // Pagination calculations
    const sortedBookcase = sortBookcase(bookcase);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentBooks = sortedBookcase.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sortedBookcase.length / itemsPerPage);

    const nextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const goToPage = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    useEffect(() => { getBookcase() }, []);

    return (
        <div className="flex flex-col p-6 mx-auto max-w-6xl bg-white rounded-lg shadow-lg">
            <button
                type="button"
                className="self-start inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 hover:text-blue-900 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors"
                onClick={() => { successHandler(false) }}
                title="Back to library"
            >
                <span aria-hidden="true">←</span>
                <span>Back to library</span>
            </button>

            <div className="border-2 border-gray-300 rounded-lg mt-5 bg-gray-50">
                {currentBook ?
                    <BookTitle book_title={currentBook} successHandler={(state) => setBook(state)} />
                    :
                    <div className="flex flex-col p-6">
                        <div className="items-center flex flex-col">
                            <p className="font-bold text-2xl text-blue-600 mb-6">Add a Book/Movie</p>
                            <form className="flex flex-col border border-gray-300 shadow-md mx-auto p-6 rounded-lg bg-white w-full max-w-4xl">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                                    <div>
                                        <p className="font-medium text-gray-700 mb-2">Author</p>
                                        <input
                                            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            type="text"
                                            placeholder="Author"
                                            value={newAuthor}
                                            onChange={e => setAuthor(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-700 mb-2">Title</p>
                                        <input
                                            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            type="text"
                                            placeholder="Title"
                                            value={newTag}
                                            onChange={e => setTag(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-700 mb-2">Language</p>
                                        <DropdownLang
                                            selectHandler={(event) => {
                                                setLang(event.target.value);
                                            }}
                                            elValue={lang}
                                        />
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-700 mb-2">Book or Movie?</p>
                                        <DropdownType
                                            selectHandler={(event) => {
                                                setType(event.target.value);
                                            }}
                                            elValue={type}
                                        />
                                    </div>
                                </div>

                                {newBookcaseError && (
                                    <div className="text-red-500 text-center font-medium mb-4 p-2 bg-red-50 rounded-md">
                                        {newBookcaseError}
                                    </div>
                                )}

                                <button
                                    className="p-3 mx-auto mt-2 rounded-md bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors w-full max-w-xs"
                                    onClick={addCreateBookcase}
                                >
                                    Add to Bookcase
                                </button>
                            </form>

                            {changeList?.length > 0 &&
                                <div className="flex flex-col border border-gray-300 bg-white shadow-md p-5 my-5 rounded-lg w-full max-w-4xl">
                                    <h3 className="text-xl font-bold text-gray-700 mb-4 text-center">Pending Changes</h3>
                                    {changeList.map((change, i) => {
                                        return (
                                            <div key={i} className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center p-3 border-b border-gray-200 last:border-b-0">
                                                <div className="text-gray-700 font-medium text-center">{change.author}</div>
                                                <div className="text-gray-700 font-medium text-center">{change.title}</div>
                                                <div className="text-gray-700 font-medium text-center">{change.language}</div>
                                                <div className="text-gray-700 font-medium text-center">{change.type}</div>
                                                <div className="flex flex-col md:flex-row gap-2 justify-center">
                                                    <span className={`text-sm font-semibold p-2 rounded ${change.action === 'CREATE' ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100'}`}>
                                                        {change.action}
                                                    </span>
                                                    <button
                                                        className="border border-red-500 bg-red-50 text-red-500 font-semibold p-2 rounded hover:bg-red-100 transition-colors"
                                                        onClick={() => { removeChange(i) }}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                    })}
                                    <button
                                        className="border-2 bg-green-500 hover:bg-green-600 mx-auto text-white font-bold p-3 rounded-lg mt-4 transition-colors w-full max-w-xs"
                                        onClick={commit}
                                    >
                                        Commit All Changes
                                    </button>
                                </div>
                            }
                        </div>

                        <div className="py-6 my-6">
                            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                                <h3 className="text-2xl font-bold text-gray-800">Your Bookcase</h3>
                                
                                {/* Sorting Controls */}
                                {bookcase.length > 0 && (
                                    <div className="flex flex-wrap gap-2 items-center">
                                        <span className="text-sm font-medium text-gray-700">Sort by:</span>
                                        <button
                                            onClick={() => handleSort("date")}
                                            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                                sortBy === "date" 
                                                ? "bg-blue-500 text-white" 
                                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                            }`}
                                        >
                                            Date Added {getSortIcon("date")}
                                        </button>
                                        <button
                                            onClick={() => handleSort("title")}
                                            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                                sortBy === "title" 
                                                ? "bg-blue-500 text-white" 
                                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                            }`}
                                        >
                                            Title {getSortIcon("title")}
                                        </button>
                                        <button
                                            onClick={() => handleSort("author")}
                                            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                                sortBy === "author" 
                                                ? "bg-blue-500 text-white" 
                                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                            }`}
                                        >
                                            Author {getSortIcon("author")}
                                        </button>
                                        <button
                                            onClick={() => handleSort("language")}
                                            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                                sortBy === "language" 
                                                ? "bg-blue-500 text-white" 
                                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                            }`}
                                        >
                                            Language {getSortIcon("language")}
                                        </button>
                                    </div>
                                )}
                            </div>
                            
                            {bookcase.length > 0 ? (
                                <>
                                    <div className="grid grid-cols-1 gap-4 mx-auto w-full max-w-5xl">
                                        {currentBooks.map((book, i) => {
                                            return (
                                                <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center border border-gray-300 shadow-sm p-4 rounded-lg bg-white hover:shadow-md transition-shadow">
                                                    <div className="text-gray-800 font-medium p-2 text-center md:text-left">
                                                        <span className="text-sm text-gray-500 block md:hidden">Author: </span>
                                                        {book.author}
                                                    </div>
                                                    <div className="text-gray-800 font-medium p-2 text-center md:text-left">
                                                        <span className="text-sm text-gray-500 block md:hidden">Title: </span>
                                                        {book.tag}
                                                    </div>
                                                    <button
                                                        className="border border-blue-500 bg-blue-50 text-blue-600 rounded-md p-2 hover:bg-blue-100 transition-colors font-medium"
                                                        onClick={() => {
                                                            if (book.hasOwnProperty("type")) {
                                                                setBook({ author: book.author, title: book.tag, language: book.language, type: book.type })
                                                            } else {
                                                                setBook({ author: book.author, title: book.tag, language: book.language, type: null })
                                                            }
                                                        }}
                                                    >
                                                        Info
                                                    </button>
                                                    <button
                                                        className="border border-red-500 bg-red-50 text-red-600 font-semibold p-2 rounded hover:bg-red-100 transition-colors"
                                                        onClick={() => {
                                                            if (book.hasOwnProperty("type")) {
                                                                addDeleteBookcase({ language: book.language, author: book.author, title: book.tag, type: book.type })
                                                            } else {
                                                                addDeleteBookcase({ language: book.language, author: book.author, title: book.tag, type: null })
                                                            }
                                                        }}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            )
                                        })}
                                    </div>

                                    {/* Pagination Controls */}
                                    {totalPages > 1 && (
                                        <div className="flex flex-col sm:flex-row justify-between items-center mt-8 gap-4">
                                            <div className="text-gray-700 font-medium">
                                                Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, sortedBookcase.length)} of {sortedBookcase.length} items
                                                <span className="text-sm text-gray-500 ml-2">
                                                    (Sorted by {getSortLabel(sortBy)} - {getSortDescription()})
                                                </span>
                                            </div>
                                            
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={prevPage}
                                                    disabled={currentPage === 1}
                                                    className={`px-4 py-2 rounded-md font-medium ${currentPage === 1 
                                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                                                        : 'bg-blue-500 text-white hover:bg-blue-600'}`}
                                                >
                                                    Previous
                                                </button>
                                                
                                                <div className="flex gap-1">
                                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                                        <button
                                                            key={page}
                                                            onClick={() => goToPage(page)}
                                                            className={`w-10 h-10 rounded-md font-medium ${currentPage === page 
                                                                ? 'bg-blue-500 text-white' 
                                                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                                                        >
                                                            {page}
                                                        </button>
                                                    ))}
                                                </div>
                                                
                                                <button
                                                    onClick={nextPage}
                                                    disabled={currentPage === totalPages}
                                                    className={`px-4 py-2 rounded-md font-medium ${currentPage === totalPages 
                                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                                                        : 'bg-blue-500 text-white hover:bg-blue-600'}`}
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="text-center text-gray-500 py-8 border-2 border-dashed border-gray-300 rounded-lg">
                                    Your bookcase is empty. Add some books or movies to get started!
                                </div>
                            )}
                        </div>
                    </div>
                }
            </div>
        </div>
    )
}

export default BookCase;